import { useEffect } from "react";
import { Helmet } from "react-helmet-async";

const MAX_TITLE_LENGTH = 59;
const CANONICAL_HOST = "mvibeon.com";
const CANONICAL_ORIGIN = `https://${CANONICAL_HOST}`;
const DEFAULT_OG_IMAGE = `${CANONICAL_ORIGIN}/__l5e/assets-v1/0792666a-cef6-4e36-8bf0-b54d797bf654/og-image-mvibe.jpg`;

const clampTitle = (value: string) => {
  const trimmed = value.trim();
  return trimmed.length > MAX_TITLE_LENGTH ? trimmed.slice(0, MAX_TITLE_LENGTH).trimEnd() : trimmed;
};

/**
 * Returns true when the page is being served from a host other than the
 * canonical mvibeon.com (e.g. preview/staging subdomains, localhost).
 * On those hosts we force noindex so Google never indexes duplicate
 * copies of the site.
 */
const isNonCanonicalHost = () => {
  if (typeof window === "undefined") return false;
  const host = window.location.hostname;
  // Production prerender runs against localhost. Treat it as canonical so the
  // generated static HTML never captures preview-only `noindex` robots tags.
  if (host === "localhost" || host === "127.0.0.1" || host === "::1") return false;
  return host !== CANONICAL_HOST && host !== `www.${CANONICAL_HOST}`;
};

/**
 * Always returns an absolute canonical URL on mvibeon.com, regardless of
 * the host the page is currently being served from. Falls back to the
 * provided canonical (assumed to already be absolute) if window is unavailable.
 */
const stripTrailingSlash = (url: string) => {
  // Keep root "/" intact, strip trailing slash from every other path
  try {
    const u = new URL(url);
    if (u.pathname !== "/" && u.pathname.endsWith("/")) {
      u.pathname = u.pathname.replace(/\/+$/, "");
    }
    return u.toString();
  } catch {
    return url;
  }
};

const resolveCanonical = (canonical?: string) => {
  if (typeof window === "undefined") return canonical;
  const pathname = window.location.pathname + window.location.search;
  let resolved: string;
  if (canonical && canonical.startsWith(CANONICAL_ORIGIN)) {
    resolved = canonical;
  } else if (canonical && canonical.startsWith("http")) {
    try {
      const u = new URL(canonical);
      resolved = `${CANONICAL_ORIGIN}${u.pathname}${u.search}`;
    } catch {
      resolved = `${CANONICAL_ORIGIN}${pathname}`;
    }
  } else {
    resolved = `${CANONICAL_ORIGIN}${pathname}`;
  }
  return stripTrailingSlash(resolved);
};

interface PreloadImage {
  href: string;
  imagesrcset?: string;
  imagesizes?: string;
  type?: string;
}

interface SEOHeadProps {
  title: string;
  description: string;
  canonical?: string;
  type?: string;
  noIndex?: boolean;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  schema?: Record<string, unknown> | Array<Record<string, unknown>>;
  preloadImages?: Array<string | PreloadImage>;
}

const SEOHead = ({
  title,
  description,
  canonical,
  type = "website",
  noIndex = false,
  ogTitle,
  ogDescription,
  ogImage,
  schema,
  preloadImages = [],
}: SEOHeadProps) => {
  const resolvedTitle = clampTitle(title);
  const resolvedOgTitle = clampTitle(ogTitle ?? resolvedTitle);
  const resolvedOgDescription = ogDescription ?? description;
  const resolvedCanonical = resolveCanonical(canonical);
  const resolvedOgImage = ogImage ?? DEFAULT_OG_IMAGE;

  // Force noindex when the page isn't served from the canonical domain,
  // OR when the caller explicitly requested noIndex.
  const shouldNoIndex = noIndex || isNonCanonicalHost();
  const robotsContent = shouldNoIndex ? "noindex, nofollow" : "index, follow";

  // Manage <link rel="canonical"> imperatively to guarantee exactly ONE
  // canonical exists in the DOM at any time (Helmet doesn't dedupe by rel).
  useEffect(() => {
    if (!resolvedCanonical || typeof document === "undefined") return;
    const existing = document.querySelectorAll('link[rel="canonical"]');
    existing.forEach((el, idx) => {
      if (idx > 0) el.remove();
    });
    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", resolvedCanonical);
  }, [resolvedCanonical]);

  // Manage preload <link> tags imperatively — react-helmet-async can't handle
  // the imagesrcset/imagesizes/fetchpriority attributes (throws on non-string props).
  useEffect(() => {
    if (typeof document === "undefined") return;
    const created: HTMLLinkElement[] = [];
    preloadImages.filter(Boolean).forEach((item) => {
      const p: PreloadImage = typeof item === "string" ? { href: item } : item;
      const link = document.createElement("link");
      link.rel = "preload";
      link.as = "image";
      link.href = p.href;
      link.setAttribute("fetchpriority", "high");
      if (p.imagesrcset) link.setAttribute("imagesrcset", p.imagesrcset);
      if (p.imagesizes) link.setAttribute("imagesizes", p.imagesizes);
      if (p.type) link.type = p.type;
      document.head.appendChild(link);
      created.push(link);
    });
    return () => {
      created.forEach((l) => l.remove());
    };
  }, [preloadImages]);

  return (
    <Helmet>
      <title>{resolvedTitle}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={robotsContent} />

      {/* Open Graph */}
      <meta property="og:title" content={resolvedOgTitle} />
      <meta property="og:description" content={resolvedOgDescription} />
      <meta property="og:type" content={type} />
      {resolvedCanonical && <meta property="og:url" content={resolvedCanonical} />}
      <meta property="og:image" content={resolvedOgImage} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={resolvedOgTitle} />
      <meta name="twitter:description" content={resolvedOgDescription} />
      <meta name="twitter:image" content={resolvedOgImage} />

      {/* Canonical + preload are managed via useEffect above */}


      {/* JSON-LD Schema */}
      {schema && (
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      )}
    </Helmet>
  );
};

export default SEOHead;
